/* import __COLOCATED_TEMPLATE__ from './project-details-action-status.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import { showModal } from 'sh-www/utils/modal-controls';
import { APPLICATION } from "sh-www/models/application";
import { ASSIGNMENT } from "sh-www/models/assignment";
import { BILLABLE_RECORD } from "sh-www/models/billable-record";
import LightboxOverlay from 'sh-www/components/modals/overlays/lightbox';
import { EMBER_ROUTE_NAMES } from "sh-www/constants/routes";
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';

class ProjectDetailsActionStatus extends Component {
  @service('commands/application-commands') applicationCommandService;
  @service('state-managers/application') applicationStateManager;
  @service('commands/thread-commands') threadCommandService;
  @service('error-tracker') errorTracker;
  @service router;
  @service session;
  @service store;

  get isShowViewPublishedProject() {
    const { status, projectdigest } = this.args.application;
    return (status === APPLICATION.STATUS.COMPLETED || status === APPLICATION.STATUS.PAID) && projectdigest?.link;
  }

  get helpText() {
    const { application } = this.args;
    const buyerFirstName = application.buyer.firstName;
    const sellerFirstName = application.seller.firstName;
    let helpText = '';

    if (this.session.currentUser.isBuyer) {
      switch (application.status) {
        case APPLICATION.STATUS.PENDING:
          if (application.isNeedsOffer) {
            helpText = `You created a project with ${sellerFirstName} but have not yet made an offer`;
          }
          break;
        case APPLICATION.STATUS.PUBLISHER_APPROVED:
          break;
        case APPLICATION.STATUS.PRODUCER_APPROVED:
          break;
        case APPLICATION.STATUS.PUBLISHER_APPROVED_PRICE:
          helpText = `You accepted ${sellerFirstName}\'s offer. ${sellerFirstName} needs to sign the contract to start this project.`;
          break;
        case APPLICATION.STATUS.ACTIVE:
          break;
        case APPLICATION.STATUS.COMPLETE_PRODUCER:
          helpText = `${sellerFirstName} marked this project as complete. You need to confirm this.`;
          break;
        case APPLICATION.STATUS.CANCEL_PENDING:
          helpText = `You requested this project to be cancelled. ${sellerFirstName} needs to confirm this to cancel the project.`;
          break;
        case APPLICATION.STATUS.COMPLETE_PUBLISHER:
          helpText = `You marked this project as complete. ${sellerFirstName} needs to confirm this.`;
          break;
        case APPLICATION.STATUS.COMPLETED:
          break;
        case APPLICATION.STATUS.PAID:
          break;
        case APPLICATION.STATUS.DECLINED:
          break;
        case APPLICATION.STATUS.CANCELLED:
          break;
      }

      return helpText;
    }

    switch (application.status) {
      case APPLICATION.STATUS.PENDING:
        break;
      case APPLICATION.STATUS.PUBLISHER_APPROVED:
        break;
      case APPLICATION.STATUS.PRODUCER_APPROVED:
        break;
      case APPLICATION.STATUS.PUBLISHER_APPROVED_PRICE:
        helpText = `${buyerFirstName} accepted your offer. You need to sign the contract to start this project.`;
        break;
      case APPLICATION.STATUS.ACTIVE:
        break;
      case APPLICATION.STATUS.COMPLETE_PRODUCER:
        helpText = `You marked this project as complete. ${buyerFirstName} needs to confirm this.`;
        break;
      case APPLICATION.STATUS.CANCEL_PENDING:
        helpText = `${buyerFirstName} requested this project to be cancelled. Confirm the cancellation or discuss this further with ${buyerFirstName} if needed.`;
        break;
      case APPLICATION.STATUS.COMPLETE_PUBLISHER:
        helpText = `${buyerFirstName} marked this project as complete. You need to confirm this.`;
        break;
      case APPLICATION.STATUS.COMPLETED:
        break;
      case APPLICATION.STATUS.PAID:
        break;
      case APPLICATION.STATUS.DECLINED:
        break;
      case APPLICATION.STATUS.CANCELLED:
        break;
    }

    return helpText;
  }

  get isShowAccept() {
    return this.session.currentUser.isBuyer
      ? APPLICATION.STATUS.PRODUCER_APPROVED === this.args.application.status
      : APPLICATION.STATUS.PUBLISHER_APPROVED === this.args.application.status;
  }

  get isShowDeclineAdvance() {
    if (this.session.currentUser.isBuyer) {
      return this.args.application.advanceShInvoiceNeedsAgreement?.agreement === BILLABLE_RECORD.AGREEMENT.SELLER_APPROVED;
    } else {
      return this.args.application.advanceShPaymentNeedsAgreement?.agreement === BILLABLE_RECORD.AGREEMENT.BUYER_APPROVED;
    }
  }

  get menuDropdownOptions() {
    const menuOptions = []
    const { application } = this.args;
    const addMakeNewOfferOptions = () => {
      menuOptions.push({
        title: 'Make new offer',
        onClick: () => this.hireFreelancer(),
      });
    };
    const addDeclineAdvanceOption = () => {
      menuOptions.push({
        title: 'Decline advance',
        onClick: () => this.onDeclineAdvanceClicked(),
      });
    };
    const addRequestAdvanceOption = () => {
      menuOptions.push({
        title: 'Request advance',
        onClick: () => this.onPayAdvanceClicked(),
      });
    };
    const addRequestCancellationOption = () => {
      menuOptions.push({
        title: 'Request cancellation',
        onClick: () => this.onRequestCancellationClicked(),
        dataTestId: 'project-details__request-cancellation__menu-button'
      });
    };
    const addDeclineOption = () => {
      menuOptions.push({
        title: 'Decline',
        onClick: () => this.onDeclineClicked(),
      });
    };
    const addUndeclineOption = () => {
      menuOptions.push({
        title: 'Un-decline',
        onClick: () => this.onUndeclineClicked(),
      });
    };
    const addEditLinkToPublishedProject = () => {
      menuOptions.push({
        title: 'Edit link to published project',
        onClick: () => this.addOrEditLinkToPublishProject(),
      });
    };
    const addWithdrawOption = () => {
      menuOptions.push({
        title: 'Withdraw',
        onClick: () => this.onWithdrawProposalClicked(),
      });
    };
    const addEditTitleOption = () => {
      menuOptions.push({
        title: 'Change project name',
        onClick: () => showModal(this, 'projects/edit-project-title', { application }),
      });
    };

    if (this.isShowDeclineAdvance) {
      addDeclineAdvanceOption();
    }

    if (this.session.currentUser.isBuyer) {
      switch (application.status) {
        case APPLICATION.STATUS.PENDING:
          if (application.isNeedsOffer) {
            addDeclineOption();
          }
          break;

        case APPLICATION.STATUS.PUBLISHER_APPROVED:
          addDeclineOption();
          break;
        case APPLICATION.STATUS.PRODUCER_APPROVED:
          addDeclineOption();
          break;
        case APPLICATION.STATUS.PUBLISHER_APPROVED_PRICE:
          addDeclineOption();
          break;
        case APPLICATION.STATUS.ACTIVE:
          if (!application.isAdvancePending) {
            addRequestCancellationOption();
          }
          break;
        case APPLICATION.STATUS.COMPLETE_PRODUCER:
          if (!application.isAdvancePending) {
            addRequestCancellationOption();
          }
          break;
        case APPLICATION.STATUS.CANCEL_PENDING:
          break;
        case APPLICATION.STATUS.COMPLETE_PUBLISHER:
          if (!application.isAdvancePending) {
            addRequestCancellationOption();
          }
          break;
        case APPLICATION.STATUS.COMPLETED:
          break;
        case APPLICATION.STATUS.PAID:
          break;
        case APPLICATION.STATUS.DECLINED:
          addMakeNewOfferOptions();
          addUndeclineOption();
          break;
        case APPLICATION.STATUS.CANCELLED:
          break;
      }

      if (!application.pitch) {
        addEditTitleOption();
      }
    } else {
      switch (application.status) {
        case APPLICATION.STATUS.PENDING:
          addWithdrawOption();
          break;
        case APPLICATION.STATUS.PUBLISHER_APPROVED:
          addDeclineOption();
          break;
        case APPLICATION.STATUS.PRODUCER_APPROVED:
          addDeclineOption();
          break;
        case APPLICATION.STATUS.PUBLISHER_APPROVED_PRICE:
          addDeclineOption();
          break;
        case APPLICATION.STATUS.ACTIVE:
          if (!application.isAdvancePending && application.isAdvanceSafe) {
            addRequestAdvanceOption();
          }
          break;
        case APPLICATION.STATUS.COMPLETE_PRODUCER:
          break;
        case APPLICATION.STATUS.CANCEL_PENDING:
          break;
        case APPLICATION.STATUS.COMPLETE_PUBLISHER:
          break;
        case APPLICATION.STATUS.COMPLETED:
        case APPLICATION.STATUS.PAID:
          if (application.projectdigest?.link) {
            addEditLinkToPublishedProject();
          }
          break;
        case APPLICATION.STATUS.DECLINED:
          break;
        case APPLICATION.STATUS.CANCELLED:
          break;
      }
    }

    return menuOptions;
  }

  get isShowConfirmCancellation() {
    return !this.session.currentUser.isBuyer && this.args.application.status === APPLICATION.STATUS.CANCEL_PENDING;
  }

  get isShowConfirmCompletion() {
    const { status } = this.args.application;
    return (!this.session.currentUser.isBuyer && status === APPLICATION.STATUS.COMPLETE_PUBLISHER)
      || (this.session.currentUser.isBuyer && status === APPLICATION.STATUS.COMPLETE_PRODUCER);
  }

  get isShowCounter() {
    const { status } = this.args.application;
    return this.session.currentUser.isBuyer
      ? APPLICATION.STATUS.PRODUCER_APPROVED === status
      : APPLICATION.STATUS.PUBLISHER_APPROVED === status;
  }

  get isShowDecline() {
    const { status, isNeedsOffer } = this.args.application;
    return this.session.currentUser.isBuyer && APPLICATION.STATUS.PENDING === status && !isNeedsOffer;
  }

  get showAddLinkToPublishedProject() {
    const { status, projectdigest } = this.args.application;
    if (
      this.session.currentUser.isBuyer
      || (status !== APPLICATION.STATUS.COMPLETED && status !== APPLICATION.STATUS.PAID)
      || !projectdigest
      || projectdigest.link
    ) {
      return false;
    }

    return true;
  }

  get isShowMakeOffer() {
    return this.session.currentUser.isBuyer && APPLICATION.STATUS.PENDING === this.args.application.status;
  }

  get isShowMarkCompleted() {
    return this.args.application.status === APPLICATION.STATUS.ACTIVE && !this.isShowCancelAdvance && !this.isShowReviewAdvance;
  }

  get isShowPayAdvance() {
    const { isAdvancePending, status } = this.args.application;
    return this.session.currentUser.isBuyer && !isAdvancePending && status === APPLICATION.STATUS.ACTIVE;
  }

  get isShowCancelAdvance() {
    const { advanceShInvoiceNeedsAgreement, advanceShPaymentNeedsAgreement } = this.args.application;
    return this.session.currentUser.isBuyer
      ? advanceShInvoiceNeedsAgreement?.agreement === BILLABLE_RECORD.AGREEMENT.BUYER_APPROVED
      : advanceShPaymentNeedsAgreement?.agreement === BILLABLE_RECORD.AGREEMENT.SELLER_APPROVED;
  }


  get isShowReviewAdvance() {
    const { advanceShInvoiceNeedsAgreement, advanceShPaymentNeedsAgreement } = this.args.application;
    return this.session.currentUser.isBuyer
      ? advanceShInvoiceNeedsAgreement?.agreement === BILLABLE_RECORD.AGREEMENT.SELLER_APPROVED
      : advanceShPaymentNeedsAgreement?.agreement === BILLABLE_RECORD.AGREEMENT.BUYER_APPROVED;
  }

  get showMarkActive() {
    const { status } = this.args.application;
    return APPLICATION.STATUS.COMPLETE_PRODUCER === status || APPLICATION.STATUS.COMPLETE_PUBLISHER === status;
  }

  get isShowSignContract() {
    return !this.session.currentUser.isBuyer && this.args.application.status === APPLICATION.STATUS.PUBLISHER_APPROVED_PRICE;
  }

  get isShowUndoCancellation() {
    return this.session.currentUser.isBuyer && this.args.application.status === APPLICATION.STATUS.CANCEL_PENDING;
  }

  get cancelAdvanceText() {
    return this.session.currentUser.isBuyer
      ? 'Cancel advance'
      : 'Cancel advance request';
  }

  get projectStatusText() {
    const { application } = this.args;
    const { seller, buyer } = application;

    if (this.session.currentUser.isBuyer) {
      if (application.isAdvancePending) {
        if (application.advanceShInvoiceNeedsAgreement?.agreement === BILLABLE_RECORD.AGREEMENT.BUYER_APPROVED) {
          return `Waiting for ${seller.firstName} to accept your advance`;
        }
        return `Waiting for you to accept ${seller.firstName}'s advance request`;
      }

      switch (application.status) {
        case APPLICATION.STATUS.PENDING:
          return application.isNeedsOffer
            ? 'Waiting for your offer'
            : 'Waiting for your response';
        case APPLICATION.STATUS.PUBLISHER_APPROVED:
          return `Waiting for ${seller.firstName} to accept your offer`;
        case APPLICATION.STATUS.PRODUCER_APPROVED:
          return `Waiting for you to accept ${seller.firstName}'s offer`;
        case APPLICATION.STATUS.PUBLISHER_APPROVED_PRICE:
          return `Waiting for ${seller.firstName} to sign the contract`;
        case APPLICATION.STATUS.ACTIVE:
          return 'Project in progress';
        case APPLICATION.STATUS.COMPLETE_PRODUCER:
          return 'Waiting for you to confirm project completion';
        case APPLICATION.STATUS.CANCEL_PENDING:
          return `Waiting for ${seller.firstName} to confirm cancellation`;
        case APPLICATION.STATUS.COMPLETE_PUBLISHER:
          return `Waiting for ${seller.firstName} to confirm project completion`;
        case APPLICATION.STATUS.COMPLETED:
        case APPLICATION.STATUS.PAID:
          return 'Project complete';
        case APPLICATION.STATUS.DECLINED:
          return 'Project declined';
        case APPLICATION.STATUS.CANCELLED:
          return 'Project cancelled';
      }

      return 'Unknown';
    }

    if (application.isAdvancePending) {
      return application.advanceShPaymentNeedsAgreement?.agreement === BILLABLE_RECORD.AGREEMENT.SELLER_APPROVED
        ? `Waiting for ${buyer.firstName} to accept your advance request`
        : 'Waiting for you to accept an advance';
    }

    switch (application.status) {
      case APPLICATION.STATUS.PENDING:
        if (application.isNeedsOffer) {
          return `Waiting for ${buyer.firstName} to make an offer`;
        }

        return `Waiting for ${buyer.firstName}'s response`;
      case APPLICATION.STATUS.PUBLISHER_APPROVED:
        return `Waiting for you to accept ${buyer.firstName}'s offer`;
      case APPLICATION.STATUS.PRODUCER_APPROVED:
        return `Waiting for ${buyer.firstName} to accept your offer`;
      case APPLICATION.STATUS.PUBLISHER_APPROVED_PRICE:
        return 'Waiting for you to sign the contract';
      case APPLICATION.STATUS.ACTIVE:
        return 'Project in progress';
      case APPLICATION.STATUS.COMPLETE_PRODUCER:
        return `Waiting for ${buyer.firstName} to confirm project completion`;
      case APPLICATION.STATUS.CANCEL_PENDING:
        return 'Waiting for you to confirm cancellation';
      case APPLICATION.STATUS.COMPLETE_PUBLISHER:
        return `Waiting for you to confirm project completion`;
      case APPLICATION.STATUS.COMPLETED:
      case APPLICATION.STATUS.PAID:
        return 'Project complete';
      case APPLICATION.STATUS.DECLINED:
        return 'Project declined';
      case APPLICATION.STATUS.CANCELLED:
        return 'Project cancelled';
    }

    return 'Unknown';
  }

  @action
  onDeclineAdvanceClicked() {
    const modalModel = {
      titleText: 'Decline advance',
      bodyText: 'Are you sure you want to decline the advance request?',
      actionButtonText: 'Decline advance',
      isShowCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmAction: () => {
        this.handleCancelAdvance.perform(this.args.application);
      }
    };
    showModal(this, 'confirmation', modalModel);
  }

  @action
  onCancelAdvanceClicked() {
    const modalModel = {
      titleText: 'Cancel advance',
      bodyText: 'Are you sure you want to cancel your advance request?',
      actionButtonText: 'Cancel advance',
      isShowCancelButton: true,
      cancelButtonText: 'Do not cancel',
      confirmAction: () => {
        this.handleCancelAdvance.perform(this.args.application);
      }
    };
    showModal(this, 'confirmation', modalModel);
  }

  @task
  *handleCancelAdvance(application) {
    try {
      yield this.applicationCommandService.cancelPendingAdvances(application);
      this.applicationStateManager.addToast('Advance cancelled');
      this.args.loadPaymentData.perform(application);
      this.args.loadThreadData.perform(application);
    } catch (e) {
      handleErrorResponseWithModal(
        this,
        e,
        'There was a problem cancelling advance. Please refresh and try again.',
        'markComplete -- error marking app complete app id: ' + application.id
      );
    }
  }

  @action
  onPayAdvanceClicked() {
    const { application } = this.args;
    if (application.isAdvanceSafe) {
      this.router.transitionTo(EMBER_ROUTE_NAMES.PROJECT__ADVANCE, application.id);
      return;
    }

    showModal(this, 'unsafe-advance-warning', {
      proceedAction: () => {
        this.router.transitionTo(EMBER_ROUTE_NAMES.PROJECT__ADVANCE, application.id);
      },
    });
  }

  @action
  onReviewAdvanceClicked() {
    this.router.transitionTo(EMBER_ROUTE_NAMES.PROJECT__ADVANCE__APPROVE, this.args.application.id);
  }

  @action
  onUndeclineClicked() {
    const { application } = this.args;
    showModal(this, 'undecline-modal', {
      seller: application.seller,
      application: application,
      undeclineApplication: () => this.undeclineApplication.perform(application)
    });
  }

  @task
  *undeclineApplication(application) {
    try {
      yield this.applicationCommandService.undeclineApplication(application.id);
      this.applicationStateManager.addToast('Application un-declined');
      this.args.loadPaymentData.perform(application);
      this.args.loadThreadData.perform(application);
    } catch (e) {
      this.applicationStateManager.addToast('There was a problem un-declining this application. Please try again.');
      this.errorTracker.error('undeclineApplication - ' + application.id, this, e);
    }
  }

  @action
  onUndoCancellationClicked() {
    const modalModel = {
      titleText: 'Undo cancellation',
      bodyText: `This project will become active again and ${this.args.application.seller.firstName} will be notified.`,
      actionButtonText: 'Undo cancellation',
      isShowCancelButton: true,
      confirmAction: () => {
        this.undoCancellation.perform(this.args.application);
      }
    };
    showModal(this, 'confirmation', modalModel);
  }

  @task
  *undoCancellation(application) {
    try {
      yield this.applicationCommandService.withdrawCancellation(application);
      this.applicationStateManager.addToast('Cancellation undone');
      this.args.loadPaymentData.perform(application);
      this.args.loadThreadData.perform(application);
    } catch (e) {
      this.applicationStateManager.addToast('There was problem undoing cancellation. Please try again.');
      this.errorTracker.error('undoCancellation - ' + application.id, this, e);
    }
  }

  @action
  onDeclineClicked() {
    const { application } = this.args;
    const modalModel = {
      title: this.session.currentUser.isBuyer ?
        `Decline ${application.seller.firstName}'s proposal` :
        'Decline this project',
      textAreaPlaceholder: this.session.currentUser.isBuyer ?
        `Optional: Leave a note for ${application.seller.firstName}` :
        'Optional: Leave a note',
      cancelText: 'Cancel',
      confirmText: 'Decline',
      onConfirmMessage: (message) => {
        this.declineApplication.perform(application, message);
      }
    };
    showModal(this, 'lsg/lsg-modal-text-editor', modalModel);
  }

  @task
  *declineApplication(application, opt_message = '') {
    try {
      yield this.applicationCommandService.declineApplication(application, opt_message);

      this.session.currentUser.isBuyer
        ? this.applicationStateManager.addToast('Application declined')
        : this.applicationStateManager.addToast('Project declined');

      this.args.loadPaymentData.perform(application);
      this.args.loadThreadData.perform(application);

    } catch (e) {
      handleErrorResponseWithModal(
        this,
        e,
        'There was an error declining the project. Please refresh and try again.',
        'declineApplication -- error marking app declined app id: ' + application.id
      );
    }
  }

  @action
  addOrEditLinkToPublishProject() {
    const { projectdigest } = this.args.application;
    const isAddLinkToPublishedProject = true;
    showModal(this, 'work-samples/project', { projectDigest: projectdigest, isAddLinkToPublishedProject });
  }

  @action
  onWithdrawProposalClicked() {
    const { application } = this.args;
    const modalModel = {
      title: `Withdraw your application`,
      textAreaPlaceholder: `Optional: Leave a note`,
      cancelText: 'Cancel',
      confirmText: 'Withdraw',
      onConfirmMessage: (message) => {
        this.withdrawProposal.perform(application, message);
      }
    };
    showModal(this, 'lsg/lsg-modal-text-editor', modalModel);
  }

  @task
  *withdrawProposal(application, withdrawMessage) {
    try {
      if (withdrawMessage) {
        yield this.saveNewMessage.perform(application, withdrawMessage);
      }
      yield this.applicationCommandService.declineApplication(application, withdrawMessage);
      this.args.loadPaymentData.perform(application);
      this.args.loadThreadData.perform(application);
    } catch (e) {
      handleErrorResponseWithModal(
        this,
        e,
        'There was an error withdrawing proposal. Please refresh and try again.',
        'withdrawProposal -- error withdrawing proposal app id: ' + application.id
      );
    }
  }

  @task
  *saveNewMessage(application, newMessage, assetIDs = []) {
    try {
      const thread = application.thread;
      const includes = ['user','assets.uri-collection','actions'];
      yield this.threadCommandService.addNewMessageToThread(thread, newMessage, assetIDs, includes);
    } catch (e) {
      this.applicationStateManager.addToast('There was a problem sending message. Please try again');
    }
  }

  @action
  onRequestCancellationClicked() {
    this.router.transitionTo(EMBER_ROUTE_NAMES.PROJECT__CANCELLATION__CONFIRM, this.args.application.id);
  }

  @action
  onConfirmCompletionClicked() {
    const { isLegalSigningNeeded, legalToShow, application } = this.args;
    if (this.session.currentUser.isSeller && isLegalSigningNeeded) {
      const legalModalModel = {
        application,
        legalToShow,
        onLegalAgreed: () => {
          this.applicationCommandService.agreeToLegal(application).then(() => {
            this.args.loadPaymentData.perform(application).then(() => {
              this.router.transitionTo(EMBER_ROUTE_NAMES.PROJECT__COMPLETE, application.id);
            });
          });
        }
      }
      showModal(this, 'projects/accept-terms-modal', legalModalModel);
    } else {
      this.router.transitionTo(EMBER_ROUTE_NAMES.PROJECT__COMPLETE, application.id);
    }
  }

  @task
  *sendOffer(price, application) {
    try {
      yield this.applicationCommandService.makeOffer(application.id, price);
      this.applicationStateManager.addToast('Offer sent');
      this.args.loadPaymentData.perform(application);
      this.args.loadThreadData.perform(application);
      const showDeclineOtherApplicationsModal = this.session.currentUser.isBuyer
        && application.assignment.type === ASSIGNMENT.TYPES.STANDARD
        && !application.direct;

      if (showDeclineOtherApplicationsModal) {
        this.args.declineOtherApplicationsModal(application);
      }
    } catch (e) {
      handleErrorResponseWithModal(
        this,
        e,
        'There was an error sending offer. Please refresh and try again.',
        'sendOffer -- error app id' + application.id
      );
    }
  }

  @action
  onAcceptOfferClicked() {
    const { application, isLegalSigningNeeded, legalToShow, approveOffer } = this.args;

    if (this.session.currentUser.isSeller && isLegalSigningNeeded) {
      const legalModalModel = {
        application,
        legalToShow,
        onLegalAgreed: () => {
          this.agreeLegalAndApproveOffer.perform(application);
        }
      };
      showModal(this, 'projects/accept-terms-modal', legalModalModel);
      return;
    }

    const modalModel = {
      application,
      offerRecipientName: application.seller.name,
      initialPayoutAmount: application.price,
      isApproveOffer: true,
      isFromSeller: !this.session.currentUser.isBuyer,
      onPriceConfirmed: () => {
        approveOffer.perform(application);
      }
    };
    showModal(this, 'posting/make-offer-modal', modalModel);
  }

  @task
  *agreeLegalAndApproveOffer(application) {
    yield this.args.agreeToLegal.perform(application);
    yield this.args.approveOffer.perform(application);
  }


  @action
  onConfirmCancellationClicked() {
    this.router.transitionTo(EMBER_ROUTE_NAMES.PROJECT__CANCELLATION__PAYMENT, this.args.application.id);
  }

  hireFreelancer() {
    const { application } = this.args;
    if (application.company.isNeedsCreditCard) {
      return showModal(this, 'payments/stripe-cc-modal', {
        seller: application.seller,
        application,
        onSuccess: () => this.hireFreelancer(),
      });
    }

    showModal(this, 'posting/make-offer-modal', {
      application,
      offerRecipientName: application.seller.name,
      initialPayoutAmount: application.price,
      onPriceConfirmed: (price) => {
        this.sendOffer.perform(price, application);
      },
    });
  }

  @action
  onMakeOfferClicked() {
    this.hireFreelancer();
  }

  @action
  onCounterClicked() {
    const { application } = this.args;
    const modalModel = {
      application,
      initialPayoutAmount: application.price,
      makingCounterOffer: true,
      onPriceConfirmed: (price) => {
        this.sendOffer.perform(price, application);
      },
      ...this.session.currentUser.isBuyer && { offerRecipientName: application.seller.name },
      ...!this.session.currentUser.isBuyer && { offerRecipientName: application.buyer.name, isFromSeller: true },
    }
    showModal(this, 'posting/make-offer-modal', modalModel);
  }

  @action
  onMarkCompleteClicked() {
    const { isLegalSigningNeeded, legalToShow, application } = this.args;
    if (this.session.currentUser.isSeller && isLegalSigningNeeded) {
      const legalModalModel = {
        application,
        legalToShow,
        onLegalAgreed: () => {
          this.applicationCommandService.agreeToLegal(application).then(() => {
            this.args.loadPaymentData.perform(application).then(() => {
              this.router.transitionTo(EMBER_ROUTE_NAMES.PROJECT__COMPLETE, application.id);
            });
          });
        }
      };
      showModal(this, 'projects/accept-terms-modal', legalModalModel);
    } else {
      this.router.transitionTo(EMBER_ROUTE_NAMES.PROJECT__COMPLETE, application.id);
    }
  }

  @action
  onSignContractClicked() {
    const { application, legalToShow, agreeToLegal } = this.args;
    const legalModalModel = {
      application,
      legalToShow,
      onLegalAgreed: () => {
        agreeToLegal.perform(application);
      }
    };
    showModal(this, 'projects/accept-terms-modal', legalModalModel);
  }

  @action
  onViewPublishedProjectClicked() {
    const { projectdigest } = this.args.application;
    showModal(this, LightboxOverlay, {
      digestsToShowForVideoCarousel: [projectdigest],
    });
  }
}

export default ProjectDetailsActionStatus;
